import { Button, Logo, Typography } from "@/components/atoms";
import { BackArrowBadgeIcon, PhoneBadgeIcon } from "@/components/atoms/icons";
import { useCatalogDynatraceView } from "@/core/hooks/useCatalogDynatraceView";
import { useContentfulRevision, useTranslation, useUIColors } from "@/core/hooks";
import {
  LANDING_PAGE_ACTION,
  LANDING_PAGE_SCREEN,
} from "@/core/constants/dynatraceCatalog";
import {
  landingSpaceDetailProperties,
  trackCatalogEvent,
} from "@/core/services/trackDynatraceCatalog";
import { logger } from "@/core/logger";
import { openSupportPhoneDialer } from "@/core/utils/phoneUtils";
import {
  fetchBloomUniversalSpace,
  type BloomUniversalSpaceCardContent,
  type BloomUniversalSpaceContent,
} from "@/modules/bloom-universal";
import { BLOOM_UNIVERSAL_HUB_FALLBACK } from "@/modules/bloom-universal/constants/bloomUniversalContent.fallback";
import {
  findSpaceInHub,
  mapBloomUniversalHubFields,
} from "@/modules/bloom-universal/services/mapBloomUniversalContent";
import { resolveBloomUniversalImageUrl } from "@/modules/bloom-universal/utils/resolveImageUrl";
import { router, useLocalSearchParams } from "expo-router";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Image, Linking, Platform, Pressable, ScrollView, StyleSheet, useWindowDimensions, View } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { getHeroHeight, getInstitutionCardHeight } from "../constants/exploreCardLayout";
import { useSingleTapAction } from "@/core/hooks/useSingleTapAction";
import { DirectionalArrowIcon } from "./DirectionalArrowIcon";
import { HeroOverlayGradient } from "./HeroOverlayGradient";
import { InstitutionCard } from "./InstitutionCard";

/** Mobile's details route uses `facilitiesManagement`; Contentful's spaceKey is `facilities`. */
function toCmsSpaceKey(key: string): string {
  return key === "facilitiesManagement" ? "facilities" : key;
}

/** How far the white content card overlaps the hero image's bottom edge to round its top corners over the photo. */
const CARD_OVERLAP = 24;

/** Same as web DetailCard: in-app path via the router, otherwise an external URL. */
function openCategoryHref(href: string): void {
  if (href.startsWith("/")) {
    router.push(href as never);
    return;
  }
  Linking.openURL(href).catch(() => {
    // Non-critical external navigation — ignore failures.
  });
}

/**
 * Single reusable details screen for all 6 "Explore Bloom World" cards —
 * same layout for every category, content comes from the matching
 * Contentful `bloomUniversalSpace` entry (same fetch web's
 * useBloomUniversalSpaceContent uses). No local static copy or images, same
 * as web. Blank while the fetch is in flight; on fetch failure, falls back
 * to the matching space from the same text-only fallback JSON web uses —
 * its image fields are empty, so no photo renders, matching web.
 */
export function BloomExperienceDetailsScreen() {
  const colors = useUIColors();
  const { t, currentLanguage } = useTranslation();
  const insets = useSafeAreaInsets();
  const { width: screenWidth, height: screenHeight } = useWindowDimensions();
  const params = useLocalSearchParams<{ key?: string | string[] }>();
  const institutionCardHeight = useMemo(() => getInstitutionCardHeight(screenWidth), [screenWidth]);
  // Shorter than the shared default — the hero copy is top-anchored (title +
  // CTA pinned 40px below the hero's top edge, no body description), so a
  // shorter hero keeps the white "Key Institutions" card close beneath it.
  const heroHeight = useMemo(() => getHeroHeight(screenHeight, 0.38), [screenHeight]);

  const spaceKey = useMemo(() => {
    const raw = Array.isArray(params.key) ? params.key[0] : params.key;
    return toCmsSpaceKey(raw ?? "");
  }, [params.key]);

  const fallbackSpace = useMemo(
    () => findSpaceInHub(mapBloomUniversalHubFields(BLOOM_UNIVERSAL_HUB_FALLBACK), spaceKey),
    [spaceKey],
  );

  const [space, setSpace] = useState<BloomUniversalSpaceContent | null>(null);
  // Reload the space after an app-wide Contentful refresh (app foreground).
  const contentfulRevision = useContentfulRevision();
  useEffect(() => {
    if (!spaceKey) {
      setSpace(null);
      return undefined;
    }
    const locale = currentLanguage === "ar" ? "ar" : "en";
    let cancelled = false;
    void fetchBloomUniversalSpace(spaceKey, locale)
      .then((result) => {
        if (!cancelled) {
          setSpace(result);
        }
      })
      .catch((error) => {
        logger.warn("BloomExperienceDetailsScreen", "Contentful space fetch failed, using fallback content", { spaceKey, locale, error });
        if (!cancelled) {
          setSpace(fallbackSpace ?? null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [spaceKey, currentLanguage, fallbackSpace, contentfulRevision]);

  // Explicit 2-per-row pairs instead of flexWrap + percentage widths — wrap
  // wouldn't reliably stay at 2 columns once gap grew large enough for two
  // "48%" cards + gap to exceed the row's width.
  const institutionRows = useMemo(() => {
    const cards = space?.pageCards ?? [];
    const rows: BloomUniversalSpaceCardContent[][] = [];
    for (let i = 0; i < cards.length; i += 2) {
      rows.push(cards.slice(i, i + 2));
    }
    return rows;
  }, [space]);

  const heroImageUrl = resolveBloomUniversalImageUrl(
    space?.pageHeroImageUrl ?? space?.tileImageUrl,
  );

  const spaceDetailProps = useMemo(() => {
    if (!space || !spaceKey) {
      return undefined;
    }
    return landingSpaceDetailProperties({
      spaceKey,
      spaceName: space.title,
      experienceName: space.pageHeadline || space.title,
      locale: currentLanguage,
    });
  }, [space, spaceKey, currentLanguage]);

  useCatalogDynatraceView(
    LANDING_PAGE_SCREEN.SPACE,
    LANDING_PAGE_ACTION.SPACE_VIEWED,
    Boolean(space && spaceKey),
    spaceDetailProps,
  );

  // Rapid repeat taps would otherwise push the same screen several times.
  const runSingleTap = useSingleTapAction();

  const handleBack = useCallback(() => router.back(), []);
  const handleDiscoverMore = useCallback(() => {
    runSingleTap(() => {
      if (spaceDetailProps) {
        trackCatalogEvent({
          screen: LANDING_PAGE_SCREEN.SPACE,
          actionName: LANDING_PAGE_ACTION.SPACE_DISCOVER_MORE_CLICK,
          eventType: "action",
          properties: spaceDetailProps,
        });
      }
      // Only "Bloom Properties" has a real in-app flow today — the guest
      // property listing (same screen reached from login's "Continue as
      // Guest"). Every other category opens its CMS `pageCtaHref` externally.
      if (spaceKey === "properties") {
        router.push("/guest-home");
        return;
      }
      const href = space?.pageCtaHref;
      if (!href) {
        return;
      }
      Linking.openURL(href).catch(() => {
        // Non-critical external navigation — ignore failures.
      });
    });
  }, [runSingleTap, spaceKey, space?.pageCtaHref, spaceDetailProps]);

  // Native only — mirrors web's equivalent (BloomUniversalSpaceScreen)
  // self-guarding with `if (Platform.OS !== 'web') return null`. This
  // route (`/bloom-experience-details`) is only ever reached via native
  // navigation from BloomExperienceScreen, but had no guard of its own
  // (a direct web URL hit would otherwise still render it).
  if (Platform.OS === 'web') {
    return null;
  }

  if (!space) {
    return (
      <View style={[styles.container, { backgroundColor: colors.backgroundLevel1 }]} />
    );
  }

  return (
    <View style={[styles.container, { backgroundColor: colors.backgroundLevel1 }]}>
      {/* Fixed header — a sibling of the ScrollView, not scroll content, so
          it stays pinned above the hero image and the white content card
          scrolling beneath it. Solid white now, instead of overlaying the
          hero photo transparently. */}
      <View style={[styles.header, { paddingTop: insets.top + 8, backgroundColor: colors.white }]}>
        <Pressable onPress={handleBack} hitSlop={8} accessibilityRole="button">
          <BackArrowBadgeIcon size={44} />
        </Pressable>
        <Logo size="medium" />
        <Pressable onPress={openSupportPhoneDialer} hitSlop={8} accessibilityRole="button">
          <PhoneBadgeIcon size={44} />
        </Pressable>
      </View>

      <ScrollView
        style={styles.scrollView}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={styles.scrollContent}
      >
        <View style={[styles.heroWrapper, { height: heroHeight }]}>
          {heroImageUrl ? (
            <Image source={{ uri: heroImageUrl }} style={StyleSheet.absoluteFillObject} resizeMode="cover" />
          ) : (
            <View style={[StyleSheet.absoluteFillObject, { backgroundColor: colors.buttonDefault }]} />
          )}
          <HeroOverlayGradient />

          <View style={styles.heroCopy}>
            <Typography variant="title1" color="white" style={styles.title}>
              {space.pageHeadline || space.title}
            </Typography>
            {space.pageCtaLabel ? (
              <Button
                title={space.pageCtaLabel}
                variant="primary"
                // "dark" here, not "light" — Button computes its primary
                // text/icon color as the OPPOSITE theme's buttonDefault
                // (see Button.tsx's primaryTextColor + its cloneElement on
                // `trailing`, which always overrides whatever color a passed
                // icon has). theme="dark" resolves both to #1a1a1a, visible
                // against the white background this button actually has
                // (via the style override below) — same as Homepage's CTA.
                theme="dark"
                fullWidth={false}
                trailing={<DirectionalArrowIcon size={18} />}
                onPress={handleDiscoverMore}
                style={{ backgroundColor: colors.white, height: 34 }}
                // Figma: Font/Size/XS (14) Medium, LH 100%, letter-spacing 0.
                textStyle={styles.ctaLabel}
              />
            ) : null}
          </View>
        </View>

        <View style={[styles.contentCard, { minHeight: screenHeight - heroHeight + CARD_OVERLAP, backgroundColor: colors.white }]}>
          {institutionRows.length > 0 ? (
            <View style={styles.institutionsSection}>
              <Typography
                variant="headline"
                style={[styles.institutionsTitle, { color: colors.fontHighContrast }]}
              >
                {space.cardsSectionTitle || t("bloomExperience.keyInstitutions")}
              </Typography>
              <View style={styles.institutionsGrid}>
                {institutionRows.map((row) => (
                  <View
                    key={row.map((card) => card.cardKey).join("|")}
                    style={styles.institutionsRow}
                  >
                    {row.map((card) => {
                      const href = card.cardHref?.trim() || "";
                      return (
                        <InstitutionCard
                          key={card.cardKey}
                          title={card.title}
                          {...(card.subtitle ? { subtitle: card.subtitle } : {})}
                          {...(card.imageUrl ? { imageUrl: card.imageUrl } : {})}
                          subtitleColor={colors.bloomExperienceLabelText}
                          height={institutionCardHeight}
                          {...(href
                            ? {
                                onPress: () =>
                                  runSingleTap(() => {
                                    if (spaceDetailProps) {
                                      trackCatalogEvent({
                                        screen: LANDING_PAGE_SCREEN.SPACE,
                                        actionName:
                                          LANDING_PAGE_ACTION.SPACE_INSTITUTION_CARD_CLICK,
                                        eventType: "action",
                                        properties: spaceDetailProps,
                                      });
                                    }
                                    openCategoryHref(href);
                                  }),
                              }
                            : {})}
                        />
                      );
                    })}
                  </View>
                ))}
              </View>
            </View>
          ) : null}

          <View style={{ height: insets.bottom + 24 }} />
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    position: "relative",
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 16,
    paddingBottom: 12,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
  },
  heroWrapper: {
    width: "100%",
    justifyContent: "flex-start",
  },
  // Pinned below the hero's top edge (not bottom-anchored) — same treatment
  // as Homepage's hero copy.
  heroCopy: {
    paddingHorizontal: 24,
    paddingTop: 54,
    alignItems: "flex-start",
    // Only children here are the title and the "Discover more" CTA, so this
    // is the button's exact top offset from the title.
    gap: 50,
  },
  // "auto" follows the text's own direction, so Arabic right-aligns without
  // an isRTL branch that could double-flip against native's RTL handling.
  title: {
    textAlign: "auto",
  },
  // Figma: Font/Size/XS (14) Medium, LH 100% (= fontSize), letter-spacing 0.
  ctaLabel: {
    fontFamily: "Matter-Medium",
    fontWeight: "500",
    fontSize: 14,
    lineHeight: 14,
    letterSpacing: 0,
    textAlign: "center",
    textAlignVertical: "center",
  },
  // Overlaps the hero's bottom edge by CARD_OVERLAP so its rounded top
  // corners cut into the photo, matching the Figma bottom-sheet-over-image
  // treatment instead of a hard seam between hero and content.
  contentCard: {
    marginTop: -CARD_OVERLAP,
    borderTopLeftRadius: CARD_OVERLAP,
    borderTopRightRadius: CARD_OVERLAP,
    paddingTop: CARD_OVERLAP,
  },
  institutionsSection: {
    // Matches heroCopy's paddingHorizontal so "Key Institutions" and the
    // card grid's left edge line up with the title/description/Discover
    // more button above.
    paddingHorizontal: 24,
    // paddingVertical: 10,
    gap: 12,
  },
  institutionsTitle: {
    textAlign: "auto",
    fontFamily: "Matter-Medium",
    fontWeight: "500",
    fontSize: 18,
    lineHeight: 18,
    letterSpacing: 0,
  },
  institutionsGrid: {
    gap: 16,
  },
  institutionsRow: {
    flexDirection: "row",
    gap: 25,
  },
});

export default BloomExperienceDetailsScreen;
